import { AI_APP_IDS } from "../Trainer/AppWorkouts/apps";
import FilterState, {
    type Filter,
} from "../Trainer/AppWorkouts/apps/shared/filterState";
import { Sport } from "../common/context/sport";

export interface HomeScreenItem {
    name: string;
    label: string;
    subtitle: string;
    iconColor: string;
    navUrl: string;
    order: number;
    sports?: Sport[];
    featureFlag?: string;
    /** Optional pill rendered at the trailing edge of the row (e.g. "New"). */
    badge?: string;
    setFilter?: () => void;
}

const defaultFilters: Filter = {
    appId: null,
    position: false,
    providerIds: [],
    excludeProviderIds: [],
    tagIds: [],
    contentMode: "volleyOnly",
};

/**
 * Feature that surfaces the padel assessment entry and its dashboard.
 *
 * Not an access check — the start endpoint gates nothing on it, and a player who
 * knows the URL can take the assessment without it. It says whether this
 * environment has padel journeys and workouts authored, which is the one thing
 * the client cannot work out for itself. Grant it once content lands.
 */
export const PADEL_ASSESSMENT_FEATURE = "PADEL_ASSESSMENT";

/**
 * Cohort switch for the Club-in-Club home widget, defined in @volley/data so webby-server
 * names the same feature.
 *
 * Mostly not an access check: home-status is gated on its own `CLUB_IN_CLUB_ENABLED`
 * config, not on this, so an admin can exercise it before anyone is granted the feature.
 * The exception is the set-up banner's push (POST .../me/provision), which webby-server
 * refuses without it. This says "show this person the widget", and should be granted to a
 * segment scoped to the pilot club: everyone else would only ever see it render nothing.
 */
export { CLUB_IN_CLUB_FEATURE } from "@volley/data/club-in-club";

/**
 * The assessment entry's title by DUPR standing. Shared by the library row and the
 * home-screen recommendation so the two never disagree about what the member is being
 * offered.
 *  - not linked       → "Don't have a DUPR? Get Rated"
 *  - linked, unrated  → "Get your official DUPR rating"
 *  - officially rated → "Retake your DUPR assessment"
 */
export function duprAssessmentLabel({
    duprRated,
    duprLinked,
}: {
    duprRated: boolean;
    duprLinked: boolean;
}): string {
    if (duprRated) return "Retake your DUPR assessment";
    if (duprLinked) return "Get your official DUPR rating";
    return "Don't have a DUPR? Get Rated";
}

/**
 * A workout collection only one segment can see, e.g. a club's exclusive
 * workouts. Access is webby-server's: the workouts are linked to the segment
 * (view) in the content index, and everyone outside it is refused them. The
 * feature only decides who gets the home tile, so grant it to the same
 * segment.
 */
export interface SegmentCollection {
    /** The admin segment's key. */
    segmentKey: string;
    featureFlag: string;
    label: string;
    subtitle: string;
    iconColor: string;
    /** The sports whose home screen shows the tile; all when omitted. */
    sports?: Sport[];
    /** The `mainWorkoutItems` tile this one follows. */
    placeAfter: string;
}

export const SEGMENT_COLLECTIONS: SegmentCollection[] = [
    {
        segmentKey: "club:padel-singles:exclusive",
        featureFlag: "PADEL_SINGLES_EXCLUSIVE",
        label: "Padel Singles Workouts",
        subtitle: "Singles Court Workouts",
        iconColor: "#3CE97C",
        sports: ["PADEL"],
        placeAfter: "single_shots",
    },
];

export const SEGMENT_COLLECTION_ITEM_PREFIX = "segment:";

export function segmentCollectionPath(segmentKey: string): string {
    return `/content/apps/workouts/segment/${encodeURIComponent(segmentKey)}`;
}

/** `mainWorkoutItems` with each collection's tile after the one it follows. */
export function withSegmentCollections(
    items: HomeScreenItem[],
    collections: SegmentCollection[],
): HomeScreenItem[] {
    return items.flatMap((item) => [
        item,
        ...collections
            .filter((c) => c.placeAfter === item.name)
            .map(segmentCollectionItem),
    ]);
}

export function segmentCollectionItem(
    collection: SegmentCollection,
): HomeScreenItem {
    return {
        name: `${SEGMENT_COLLECTION_ITEM_PREFIX}${collection.segmentKey}`,
        label: collection.label,
        subtitle: collection.subtitle,
        iconColor: collection.iconColor,
        navUrl: segmentCollectionPath(collection.segmentKey),
        featureFlag: collection.featureFlag,
        sports: collection.sports,
        order: 0,
    };
}

export const mainWorkoutItems: HomeScreenItem[] = [
    {
        // Base copy is the not-linked (3a) variant; NewHomeScreenLibrary
        // overrides the title for linked-but-unrated users and drops this item
        // entirely for users who have already submitted a rating. Availability
        // is the sport alone — no feature flag.
        name: "long_form_assessment",
        label: "Don't have a DUPR? Get Rated",
        subtitle: "Take the 10-minute assessment",
        iconColor: "#3CE97C",
        navUrl: "/journeys/long-form",
        order: 0,
        sports: ["PICKLEBALL"],
        badge: "New",
    },
    {
        name: "performance_dashboard",
        label: "Know Your Game",
        subtitle: "Get an objective skills rating. Level up with Volley",
        iconColor: "#3b82f6",
        navUrl: "/performance",
        order: 0,
        sports: ["PICKLEBALL"],
    },
    {
        // Unlike its DUPR twin above, this is gated on a feature as well as the
        // sport. The DUPR assessment is content that exists in every
        // environment; the padel journeys and workouts are authored per
        // environment, and a player who taps this before they land gets a 404
        // from the start endpoint. The flag is what says "padel content is live
        // here" — nothing else can, since the client can't see the journeys.
        name: "padel_assessment",
        label: "Know Your Padel Game",
        subtitle: "Take the 10-minute assessment",
        iconColor: "#3CE97C",
        navUrl: "/journeys/padel-assessment",
        featureFlag: PADEL_ASSESSMENT_FEATURE,
        order: 0,
        sports: ["PADEL"],
        badge: "New",
    },
    {
        // The lacrosse MVP's two workout types, both Lacrosse Module (app 18)
        // workouts, and the only rows lacrosse shows (see NewHomeScreenLibrary).
        // Lacrosse is only selectable with the LACROSSE feature anyway; the
        // flag here keeps the tiles to the same trial users if that ever
        // changes.
        name: "lacrosse_passing",
        label: "Player Workouts",
        subtitle: "Feeds to one to four players, from one spot",
        iconColor: "#3CE97C",
        navUrl: "/content/apps/workouts/lacrosse/passing",
        featureFlag: "LACROSSE",
        order: 0,
        sports: ["LACROSSE"],
    },
    {
        name: "lacrosse_goalie",
        label: "Goalie Workouts",
        subtitle: "Shots on one cage or two",
        iconColor: "#3CE97C",
        navUrl: "/content/apps/workouts/lacrosse/goalie",
        featureFlag: "LACROSSE",
        order: 0,
        sports: ["LACROSSE"],
    },
    {
        name: "performance_dashboard",
        label: "Know Your Game",
        subtitle: "Level up with Volley",
        iconColor: "#3b82f6",
        navUrl: "/journeys/assessments",
        featureFlag: "ASSESSMENT_DASHBOARD_V2",
        order: 0,
        sports: ["PLATFORM_TENNIS", "TENNIS", "PADEL"],
    },
    {
        name: "quick_start",
        label: "Quick Start",
        subtitle: "Choose what to hit then press play",
        iconColor: "#4ade80",
        navUrl: "/content/apps/workouts/experimental/basic/play?tag=BASIC_FRONTCOURT_SIDE",
        order: 1,
    },
    {
        name: "ai_workouts",
        label: "Interactive Workouts",
        subtitle: "Powered by Volley's camera system",
        iconColor: "#a855f7",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: AI_APP_IDS,
            });
        },
        order: 3,
    },
    {
        name: "multi_level",
        label: "Multi Level",
        subtitle: "Train an area of your game at a chosen difficulty",
        iconColor: "#56D5EF",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [9],
            });
        },
        order: 4,
    },
    {
        name: "skill_building",
        label: "Skill Building",
        subtitle: "Sequenced workouts paired with pro instruction",
        iconColor: "#DA30D8",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [11, 18],
            });
        },
        order: 5,
    },
    {
        name: "single_shots",
        label: "Single Shots",
        subtitle: "Customize a specific shot for targeted reps",
        iconColor: "#EFA511",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", {
                ...defaultFilters,
                appId: [5],
            });
        },
        order: 6,
    },
    {
        name: "classes_at_location",
        label: "Classes",
        subtitle: "Guided classes from your home club",
        iconColor: "#3CE97C",
        navUrl: "/content/classes",
        featureFlag: "HOME_SCREEN_CLASSES",
        order: 7,
    },
    {
        name: "all_workouts",
        label: "All Workouts",
        subtitle: "Explore the complete workout library",
        iconColor: "#ffffff",
        navUrl: "/content/apps/workouts/search",
        setFilter: () => {
            FilterState.setFilter("search", { ...defaultFilters });
        },
        order: 8,
    },
];

export interface PersonalItem {
    name: string;
    label: string;
    subtitle?: string;
    navUrl: string;
    sportFilter?: string[];
    setFilter?: () => void;
}

export const personalItems: PersonalItem[] = [
    {
        name: "my_workouts",
        label: "My Workouts (Create & Play)",
        navUrl: "/content/apps/workouts/user",
        setFilter: () => {
            FilterState.setFilter("user", {
                ...defaultFilters,
                contentMode: "mixed",
            });
        },
    },
    {
        name: "favorites",
        label: "Favorites",
        navUrl: "/content/apps/workouts/favorites",
        setFilter: () => {
            FilterState.setFilter("favorites", {
                ...defaultFilters,
                contentMode: "mixed",
            });
        },
    },
    {
        name: "recently_played",
        label: "Recently Played",
        navUrl: "/content/apps/workouts/recent",
    },
    {
        name: "pti_stats",
        label: "PTI Stats",
        navUrl: "/player-pti",
        sportFilter: ["PLATFORM_TENNIS"],
    },
];
